import { useAuthContext } from 'contexts/AuthContext';
import FHIRPoster from 'data/FHIRPoster';
import { ObservationBuilder } from 'fhir-sdoh';
import moment from 'moment';
import React, { useEffect } from 'react';
import { Form, Icon, Label, Loader, Popup, Select } from 'semantic-ui-react';
import type { DropdownProps, FormProps } from 'semantic-ui-react';
import type { ResourceEntry } from 'utils/bundleNarrowing';
import { softDeleteObservation } from 'utils/softDeleteObservation';

import { parseFHIRPreference } from '../../../utils/FHIRUtils';

import type { ProfileChildBaseProps, ProfileMutate, ProfileObservationEntry, RejectionWithMessage } from './types';


/**
 * `patientId` and `consentDataMutate` are required: both flow unguarded into
 * `ObservationBuilder`/`softDeleteObservation` and
 * `props.consentDataMutate().then(...)`. See the defect note in `vitals.tsx` —
 * `patientId` originates as FHIR's `0..1` `Patient.id`.
 */
type ConsentProps = Omit<ProfileChildBaseProps, 'patientId'> & {
  /** The member's FHIR `Patient.id`. */
  patientId: string;
  /** Consent-coded Observation entries from `Profile.getConsentData`. */
  data?: ProfileObservationEntry[];
  /**
   * The observation code this control writes, forwarded by `Profile` as
   * `consentCodes[0].code` — always the literal `'CONSENT'` today. Compared
   * against `'CONSENT'` before rendering the delete icon.
   */
  code: string;
  /** Revalidates the parent's consent query after a write. */
  consentDataMutate: ProfileMutate;
  /**
   * SUSPECTED DEFECT — `Profile` passes its own `profileMutate` here as well, and
   * nothing in this component reads it. Declared rather than dropped from the call
   * site: removing a prop changes the object React renders with. The consequence
   * is that a consent write revalidates only the consent query, never the profile
   * observation query the same screen renders from.
   */
  mutate?: ProfileMutate | undefined;
};

export default function Consent(props: ConsentProps) {
  const authInfo = useAuthContext();

  const [loading, setLoading] = React.useState(false);
  const [loadingMessage, setLoadingMessage] = React.useState('');

  useEffect(() => {
    if (!props.data) {
      setLoading(true);
      setLoadingMessage('Loading consent Data...');
    } else {
      setLoading(false);
      setLoadingMessage('');
    }
  }, [props.data]);

  const [observationOption, setObservationOption] = React.useState<string | undefined>();
  const [popOpen, setpopOpen] = React.useState(false);

  const CONSENT_CODES = [
    {
      id: 'PW-CONSENT-PRINTED_ENGLISH_COPY',
      text: 'Printed (English copy)',
    },
    {
      id: 'PW-CONSENT-PRINTED_SPANISH_COPY',
      text: 'Printed (Spanish copy)',
    },
    {
      id: 'PW-CONSENT-SENT_A_TEXT_EMAIL_LINK',
      text: 'Send a text/email link',
    },
  ];
  const DROPDOWN_OPTIONS = CONSENT_CODES.map((option) => ({
    key: option.id,
    text: option.text,
    value: option.text,
  }));

  const changeOption = (value: string) => {
    setObservationOption(value);
  };

  const handlePopOpen = () => {
    setpopOpen(true);
  };
  const handleClose = () => {
    setpopOpen(false);
  };

  const handleDelete = (observationId: string) => {
    setLoading(true);

    setLoadingMessage('Deleting Consent Label...');
    setpopOpen(false);
    const resource = props?.data?.find((entry: ResourceEntry<'Observation'>) => entry?.resource?.id === observationId)?.resource;
    if (!resource) {
      setLoadingMessage('Error - Unable to delete Consent label: observation not found');
      setLoading(false);
      return;
    }
    softDeleteObservation({
      observation: resource,
      token: authInfo.token,
      patientId: props.patientId,
      practitionerId: authInfo.practitionerId,
      practitionerName: authInfo.practitionerName,
      org: props.org,
    }).then(
      () => {
        setLoadingMessage('Deletion Success. Updating Consent UI...');
        // eslint-disable-next-line @typescript-eslint/no-floating-promises -- fire-and-forget revalidation; awaiting it would order this render behind a refetch.
        props.consentDataMutate().then(() => {
          setLoadingMessage('');
          setLoading(false);
        });
      },
      (error: RejectionWithMessage) => {
        setLoadingMessage(`Error - Unable to delete Consent label: ${error.message}`);
      }
    );
  };

  const getDisplay = (code: string) => {
    const arr = code.toLowerCase().split('-');
    for (let i = 0; i < arr.length; i++) {
      arr[i] = arr[i]!.charAt(0).toUpperCase() + arr[i]!.slice(1);
    }
    const str = arr.join(' ');
    return str;
  };

  // SUSPECTED DEFECT — this calls `preventDefault()` on something that is not an
  // event. `Form.onSubmit` is declared in semantic-ui-react 2.1.5 as
  // `(event: React.FormEvent<HTMLFormElement>, data: FormProps) => void` — event
  // first, props second — but the call site below destructures the *second*
  // argument and passes it on:
  //
  //   <Form onSubmit={(e, { value }) => handleSubmit(value)}>
  //
  // so `value` here is `FormProps['value']`, which carries no `preventDefault`.
  // `<Form>` declares no `value` prop at this call site either, so the argument
  // is `undefined` in practice and the `value &&` short-circuit is the only
  // reason this does not throw. Consequences: the native form submission is
  // never prevented, and the guarded branch is dead code.
  //
  // Reported, not fixed. Correcting it means taking the event from the first
  // parameter, which would newly suppress a page reload that happens today —
  // a behaviour change, and one that could mask whatever the reload currently
  // papers over. The parameter is typed to what is actually passed —
  // `FormProps['value']` is `any` in semantic-ui-react 2.1.5.
  const handleSubmit = async (value: { preventDefault?: () => void } | undefined) => {
    /* eslint-disable-next-line @typescript-eslint/no-unused-expressions --
       short-circuit used as a statement; an `if` would rewrite the expression. */
    value && value.preventDefault!();
    setpopOpen(false);
    setLoading(true);
    setLoadingMessage('Submitting Consent Label...');

    const body: fhir4.Observation = ObservationBuilder.buildPreferenceObservation(
      props.patientId,
      new Date().toJSON(),
      { code: props.code, display: getDisplay(props.code) },
      // Not yet chosen when the form is submitted empty; the builder stamps
      // `undefined` into the Observation's value rather than refusing the write.
      observationOption?.trim() as string,
      authInfo.practitionerName
    );

    await FHIRPoster(`/Observation/`, authInfo.token, body).then(
      () => {
        setLoadingMessage('Submission Success. Updating Consent UI...');
        // eslint-disable-next-line @typescript-eslint/no-floating-promises -- fire-and-forget revalidation; awaiting it would order this render behind a refetch.
        props.consentDataMutate().then(() => {
          setLoadingMessage('');
          setLoading(false);
        });
        setObservationOption('');
      },
      (error: RejectionWithMessage) => {
        setLoadingMessage(`Error - Unable to submit Consent label: ${error.message}`);
      }
    );
  };

  const observationsList = (observation: ProfileObservationEntry, i: number, _counter: number) => {
    const parsedObservation = parseFHIRPreference(observation.resource);

    if (!props.sidebar) {
      return (
        <Popup
          key={i}
          flowing
          trigger={
            <Label style={{ margin: '0.15em' }}>
              {parsedObservation.valueString}{' '}
              {props.code === 'CONSENT' && <Icon name="delete" onClick={() => handleDelete(parsedObservation.id)} />}
            </Label>
          }
          content={
            <span key={i}>
              <Icon name="clock" />
              Last Updated By: {parsedObservation.author}.{' '}
              {moment(parsedObservation.lastUpdated).format('MMMM Do YYYY, h:mm:ss a')}.{' '}
              {observation.resource.category
                ? // `category`, `coding` and `display` are each `0..*`/`0..1`; the
                  // guard tests only the outermost, so a category with no coding
                  // throws here rather than falling back to "Manually added".
                  `From ${observation.resource.category[0]!.coding![0]!.display}`
                : 'Manually added'}
              .
            </span>
          }
          on="hover"
        />
      );
    } else {
      return (
        <Label key={i} basic style={{ margin: '0.15em' }} color={'grey'}>
          {parsedObservation.valueString}
        </Label>
      );
    }
  };

  return (
    <>
      {loading && <Loader active inline="centered" content={loadingMessage} />}
      {props?.data && (
        <>
          {props?.data
            ?.filter((observation: ProfileObservationEntry) => observation?.resource?.status !== 'entered-in-error')
            .map((observation: ProfileObservationEntry, i: number) => observationsList(observation, i, props.data!.length))}

          {!props.sidebar && (
            <Popup
              trigger={<Label as="a">+</Label>}
              content={
                <Form
                  onSubmit={(e, { value }: FormProps) =>
                    handleSubmit(value as { preventDefault?: () => void } | undefined)
                  }
                >
                  <Form.Group>
                    <Form.Field
                      name="option"
                      label="CONSENT OPTION:"
                      selectOnBlur={false}
                      control={Select}
                      search
                      key="option"
                      placeholder="Select and option"
                      value={observationOption}
                      options={DROPDOWN_OPTIONS}
                      onChange={(_e: React.SyntheticEvent<HTMLElement>, { value }: DropdownProps) =>
                        // Every option this control offers is a string; Semantic's
                        // `value` is the whole dropdown union.
                        changeOption(value as string)
                      }
                    />
                    <Form.Button primary content="Add" />
                  </Form.Group>
                </Form>
              }
              on="click"
              position="top right"
              open={popOpen}
              onOpen={handlePopOpen}
              onClose={handleClose}
            />
          )}
        </>
      )}
    </>
  );
}
